"use client";

import React, { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Textarea } from "../ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../ui/dialog";
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from "../ui/drawer";
import {
  Field,
  FieldDescription,
  FieldError,
  FieldGroup,
  FieldLabel,
  FieldLegend,
  FieldSeparator,
  FieldSet,
} from "../ui/field";
import { ScrollArea } from "../ui/scroll-area";
import { useSaveConfiguration } from "../../hooks/useSaveConfiguration";
import { generatePDFData } from "../../lib/pdf";
import { useConfiguratorContext } from "../../hooks/configurator/configurator-context";
import { SendEnquiry } from "../../app/actions";
import { useMediaQuery } from "../../hooks/use-media-query";
import { cn } from "../../lib/utils";

const formSchema = z.object({
  modelName: z.string(),
  garageCode: z.string(),
  garageCodeUrl: z.string(),
  pdfUrl: z.string(),
  message: z
    .string()
    .min(10, { message: "Message must be at least 10 characters long" }),
  salutation: z.string().min(1, { message: "Please select a salutation" }),
  firstName: z
    .string()
    .min(2, { message: "First name must be at least 2 characters long" }),
  lastName: z
    .string()
    .min(2, { message: "Last name must be at least 2 characters long" }),
  country: z
    .string()
    .min(2, { message: "Country must be at least 2 characters long" }),
  city: z
    .string()
    .min(2, { message: "City must be at least 2 characters long" }),
  email: z.string().email({ message: "Please enter a valid email address" }),
  phone: z.string().optional(),
  favorite_color: z.string().optional(),
});

function EnquiryForm({
  className,
  form,
  submitted,
  submitting,
  onSubmit,
}: {
  className?: string;
  form: ReturnType<typeof useForm<z.infer<typeof formSchema>>>;
  submitted: boolean;
  submitting: boolean;
  onSubmit: (data: z.infer<typeof formSchema>) => void;
}) {
  const {
    formState: { errors },
  } = form;

  if (submitted) {
    return (
      <div className={cn("space-y-4", className)}>
        <p className="text-lg text-center font-semibold">Thank you</p>
        <p>
          Your enquiry has been sent. You will shortly receive a confirmation
          email with more information. A member of our team will be in touch.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={form.handleSubmit(onSubmit)} className={className}>
      <FieldSet>
        <FieldLegend>Contact Information</FieldLegend>
        <FieldDescription>
          Please fill out the form below to send us a message.
        </FieldDescription>
        <FieldSeparator />
        <FieldGroup className="@container/field-group">
          <Field data-invalid={!!errors.message}>
            <FieldLabel htmlFor="message">Message</FieldLabel>
            <Controller
              name="message"
              control={form.control}
              render={({ field }) => (
                <Textarea
                  id="message"
                  placeholder="Enter your message here"
                  className="min-h-[80px] sm:min-h-[100px] resize-none"
                  aria-invalid={!!errors.message}
                  {...field}
                />
              )}
            />
            <FieldError errors={errors.message ? [errors.message] : []} />
          </Field>

          <Field data-invalid={!!errors.salutation}>
            <FieldLabel htmlFor="salutation">Salutation</FieldLabel>
            <Controller
              name="salutation"
              control={form.control}
              render={({ field }) => (
                <Select onValueChange={field.onChange} value={field.value}>
                  <SelectTrigger
                    id="salutation"
                    aria-invalid={!!errors.salutation}
                  >
                    <SelectValue placeholder="Select a salutation" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Mr.">Mr.</SelectItem>
                    <SelectItem value="Mrs.">Mrs.</SelectItem>
                    <SelectItem value="Ms.">Ms.</SelectItem>
                    <SelectItem value="Miss.">Miss.</SelectItem>
                    <SelectItem value="Dr.">Dr.</SelectItem>
                  </SelectContent>
                </Select>
              )}
            />
            <FieldError errors={errors.salutation ? [errors.salutation] : []} />
          </Field>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field data-invalid={!!errors.firstName}>
              <FieldLabel htmlFor="firstName">First Name</FieldLabel>
              <Controller
                name="firstName"
                control={form.control}
                render={({ field }) => (
                  <Input
                    id="firstName"
                    placeholder="Enter your first name"
                    aria-invalid={!!errors.firstName}
                    {...field}
                  />
                )}
              />
              <FieldError errors={errors.firstName ? [errors.firstName] : []} />
            </Field>

            <Field data-invalid={!!errors.lastName}>
              <FieldLabel htmlFor="lastName">Last Name</FieldLabel>
              <Controller
                name="lastName"
                control={form.control}
                render={({ field }) => (
                  <Input
                    id="lastName"
                    placeholder="Enter your last name"
                    aria-invalid={!!errors.lastName}
                    {...field}
                  />
                )}
              />
              <FieldError errors={errors.lastName ? [errors.lastName] : []} />
            </Field>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field data-invalid={!!errors.city}>
              <FieldLabel htmlFor="city">City</FieldLabel>
              <Controller
                name="city"
                control={form.control}
                render={({ field }) => (
                  <Input
                    id="city"
                    placeholder="Enter your city"
                    aria-invalid={!!errors.city}
                    {...field}
                  />
                )}
              />
              <FieldError errors={errors.city ? [errors.city] : []} />
            </Field>

            <Field data-invalid={!!errors.country}>
              <FieldLabel htmlFor="country">Country</FieldLabel>
              <Controller
                name="country"
                control={form.control}
                render={({ field }) => (
                  <Input
                    id="country"
                    placeholder="Enter your country"
                    aria-invalid={!!errors.country}
                    {...field}
                  />
                )}
              />
              <FieldError errors={errors.country ? [errors.country] : []} />
            </Field>
          </div>

          <Field data-invalid={!!errors.email}>
            <FieldLabel htmlFor="email">Email</FieldLabel>
            <Controller
              name="email"
              control={form.control}
              render={({ field }) => (
                <Input
                  id="email"
                  type="email"
                  placeholder="Enter your email"
                  aria-invalid={!!errors.email}
                  {...field}
                />
              )}
            />
            <FieldError errors={errors.email ? [errors.email] : []} />
          </Field>

          <Field data-invalid={!!errors.phone}>
            <FieldLabel htmlFor="phone">Phone Number (Optional)</FieldLabel>
            <Controller
              name="phone"
              control={form.control}
              render={({ field }) => (
                <Input
                  id="phone"
                  type="tel"
                  placeholder="Enter your phone number with country code (e.g +357)"
                  aria-invalid={!!errors.phone}
                  {...field}
                />
              )}
            />
            <FieldDescription>
              Include your country code for international numbers
            </FieldDescription>
            <FieldError errors={errors.phone ? [errors.phone] : []} />
          </Field>

          {/* Spam Protection */}
          <div className="hidden">
            <Controller
              name="favorite_color"
              control={form.control}
              render={({ field }) => (
                <Input id="favorite_color" tabIndex={-1} {...field} />
              )}
            />
          </div>

          <Field orientation="horizontal" className="pt-2">
            <Button
              type="submit"
              className="h-10 sm:h-11 text-base sm:text-sm font-medium w-full sm:w-auto sm:min-w-[120px]"
              disabled={submitting}
            >
              {submitting ? "Sending..." : "Submit Enquiry"}
            </Button>
          </Field>
        </FieldGroup>
      </FieldSet>
    </form>
  );
}

export default function EnquiryDialog({ label = "Request Quotation & Enquiry" }: { label?: string }) {
  const [isOpen, setIsOpen] = useState(false);
  const isDesktop = useMediaQuery("(min-width: 768px)");

  const configurator = useConfiguratorContext();
  const { configuratorName } = configurator;
  const { save, code } = useSaveConfiguration(
    configuratorName,
    configurator.getAllSelectedOptionIds()
  );

  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      modelName: configuratorName,
      message: "",
      salutation: "",
      firstName: "",
      lastName: "",
      city: "",
      country: "",
      email: "",
      phone: "",
      favorite_color: "",
    },
  });

  useEffect(() => {
    if (isOpen)
      save().then((code) => {
        form.setValue("garageCode", code);
        form.setValue(
          "garageCodeUrl",
          `${process.env.NEXT_PUBLIC_APP_URL}/garage-code/${code}`
        );
        form.setValue(
          "pdfUrl",
          `${process.env.NEXT_PUBLIC_APP_URL}/${configuratorName.toLowerCase()}/pdf?options=${generatePDFData(configuratorName, code!, configurator)}`
        );
      });
  }, [isOpen, configuratorName]);

  const onSubmit = async (data: z.infer<typeof formSchema>) => {
    const formData = new FormData();
    Object.entries(data).forEach(([key, value]) => {
      formData.append(key, value);
    });

    if (formData.get("favorite_color") !== "") {
      // Form submission is spam
      return;
    }

    const additionalData: any = {
      model: configuratorName,
      garageCode: code,
      options: [],
    };

    for (const section of configurator.getAllSections()) {
      for (const option of configurator.getSelectedOptions(section.id)) {
        additionalData.options.push({
          category: section?.metadata?.categoryTitle,
          optionCode: option?.id,
          description: option.label,
        });
      }
    }
    try {
      setSubmitting(true);
      await SendEnquiry(formData, additionalData);
      form.reset();
      setSubmitted(true);
    } catch (error) {
      console.error(error);
      alert("Failed to send enquiry.");
    }

    setSubmitting(false);
  };

  const reset = () => {
    form.reset();
    setSubmitting(false);
    setSubmitted(false);
  };

  if (isDesktop) {
    return (
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogTrigger asChild>
          <Button className="w-full bg-gray-900 hover:bg-gray-800 text-white rounded-md">
            Request Quotation & Enquiry
          </Button>
        </DialogTrigger>
        <DialogContent className="max-w-2xl overflow-y-auto max-h-[90vh]">
          {submitted ? (
            <div className="space-y-4">
              <DialogHeader>
                <DialogTitle>Thank you</DialogTitle>
                <DialogDescription>
                  Your enquiry has been sent. You will shortly receive a
                  confirmation email with more information. A member of our team
                  will be in touch.
                </DialogDescription>
              </DialogHeader>
            </div>
          ) : (
            <>
              <DialogHeader className="sr-only">
                <DialogTitle>Enquiry</DialogTitle>
              </DialogHeader>
              <EnquiryForm
                form={form}
                submitted={submitted}
                submitting={submitting}
                onSubmit={onSubmit}
              />
            </>
          )}
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Drawer open={isOpen} onOpenChange={setIsOpen}>
      <DrawerTrigger asChild>
        <Button
          className="w-full bg-gray-900 hover:bg-gray-800 text-white rounded-md"
        >
          {label}
        </Button>
      </DrawerTrigger>
      <DrawerContent className="max-h-[96vh] flex flex-col overflow-hidden">
        <DrawerHeader className="text-left flex-shrink-0">
          {submitted ? (
            <>
              <DrawerTitle>Thank you</DrawerTitle>
              <DrawerDescription>
                Your enquiry has been sent. You will shortly receive a
                confirmation email with more information. A member of our team
                will be in touch.
              </DrawerDescription>
            </>
          ) : (
            <DrawerTitle className="sr-only">Enquiry</DrawerTitle>
          )}
        </DrawerHeader>
        <div className="flex-1 overflow-y-auto">
          <EnquiryForm
            className="px-4 pb-4"
            form={form}
            submitted={submitted}
            submitting={submitting}
            onSubmit={onSubmit}
          />
        </div>
        {!submitted && (
          <DrawerFooter className="pt-2 flex-shrink-0 border-t">
            <DrawerClose asChild>
              <Button variant="outline">Cancel</Button>
            </DrawerClose>
          </DrawerFooter>
        )}
      </DrawerContent>
    </Drawer>
  );
}
